import { SETTINGS } from "@/utils/constants";

type ParsableString = string | (() => string);

interface IMeta {
  title?: ParsableString;
  metaTags?: {
    name: string;
    content: ParsableString;
  }[];
}

/**
 * We use this to track which meta tags we create so we don't interfere with other ones.
 */
const APP_CONTROLLED_META = "data-vue-app-controlled";

let activeMeta: IMeta = {};
let prevMeta: IMeta = {};

export function usePageMeta() {
  function getParsedString(val: ParsableString | undefined) {
    return typeof val === "function" ? val() : val;
  }

  /**
   * Update html head tags with active meta
   */
  function syncMetaTags() {
    const { metaTags, title } = activeMeta;

    document.title = [getParsedString(title), SETTINGS.app.name]
      .filter(Boolean)
      .join(" - ");

    // Remove any stale meta tags from the document using the key attribute we set below.
    Array.from(document.querySelectorAll(`[${APP_CONTROLLED_META}]`)).map(
      (el) => el?.parentNode?.removeChild(el)
    );

    (metaTags || [])
      .map((tagDef) => {
        const tag = document.createElement("meta");

        Object.entries(tagDef).forEach(([key, val]) => {
          const valResult = getParsedString(val);
          if (valResult) {
            tag.setAttribute(key, valResult);
          }
        });

        tag.setAttribute(APP_CONTROLLED_META, "");

        return tag;
      })
      .forEach((tag) => document.head.appendChild(tag));
  }

  function setMeta(meta: IMeta) {
    prevMeta = activeMeta;
    activeMeta = meta;
    syncMetaTags();
  }

  function resetMeta() {
    activeMeta = prevMeta;
    prevMeta = {};
    syncMetaTags();
  }

  return {
    activeMeta,
    setMeta,
    resetMeta,
  };
}
